<template>
  <div class="collapse-wrap">
    <div v-for="(faq, i) in faqs" :key="i" class="collapse-item">
      <h3 class="collapse-title">
        <button type="button" class="collapse-trigger" :aria-expanded="opened.includes(faq.id)" @click="toggle(faq.id)">
          <span class="collapse-label">{{ faq.title }}</span>
          <svg class="collapse-chevron" :class="{ active: opened.includes(faq.id) }" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </h3>
      <div class='collapse-content' :class='{ active: opened.includes(faq.id) }'>
        <div v-html='faq.text' class='lh-24'></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue"

export default {
  name: "Collapse",
  props: {
    faqs: {
      type: Array,
      default: null,
    },
    expandAll: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const opened = ref([])
    const toggle = (id) => {
      const index = opened.value.indexOf(id)
      if (index > -1) {
        opened.value.splice(index, 1)
      } else {
        opened.value.push(id)
      }
    }
    watch(
      () => props.expandAll,
      (value) => {
        opened.value = value ? props.faqs.map((faq) => faq.id) : []
      },
    )
    if (props.expandAll) {
      opened.value = props.faqs.map((faq) => faq.id)
    }

    return {
      toggle,
      opened,
    }
  },
}
</script>
<style lang='scss'>
.list-ans {
  list-style: disc;
  padding-left: 15px;
  li {
    margin-bottom: 10px;
  }
}
.collapse-wrap {
  max-width: 820px;
  width: 100%;

  // Divider-line treatment kept as before — no full container box/radius: hairline rule
  // above each row + one below the last (per request).
  .collapse-item {
    border-top: 1px solid $border;
    &:last-child {
      border-bottom: 1px solid $border;
    }
  }

  .collapse-title {
    margin: 0;

    // v2 §14 — trigger is a real button (keyboard-reachable, green focus ring)
    .collapse-trigger {
      display: flex;
      justify-content: space-between; // v2 Accordion — label left, chevron right
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 16px 20px; // v2 Accordion header padding (spec 14×16; a touch roomier for the full-width page FAQ)
      cursor: pointer;
      background: transparent;
      border: none;
      text-align: left;
      font: inherit;
      color: $link; // v2 Accordion — GREEN SemiBold trigger (was $ink dark)
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      @include focus-ring;
    }

    .collapse-label {
      flex: 1;
    }

    .collapse-chevron {
      flex-shrink: 0;
      transition: transform $dur-base $ease-out; // v2 Accordion — chevron rotates 180° in 200ms (was $dur-slow/320ms)
      &.active {
        transform: rotate(180deg);
      }
    }
  }

  .collapse-content {
    padding: 0 20px;
    height: 0;
    // v2 §11 — opacity only (height 0↔auto snaps; never animate raw height)
    transition: opacity $dur-base $ease-out;
    overflow: hidden;
    opacity: 0;
    font-size: 16px; // §8 ≥16px body floor — deviates from the accordion example's 14px; FAQ answers are long-form reading content
    line-height: 24px;
    color: $muted; // v2 Accordion — muted body text

    // v2 inline text link (guideline §Text Links) — FAQ answers are v-html, so links can't be
    // reached by a page's scoped style; style them here. Green, weight 500, underline on hover only.
    a {
      color: $link;
      font-weight: 500;
      text-decoration: none;
      &:hover,
      &:focus-visible {
        text-decoration: underline;
      }
    }

    &.active {
      height: auto;
      padding-bottom: 16px;
      opacity: 1;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .collapse-wrap .collapse-chevron,
  .collapse-wrap .collapse-content {
    transition: none;
  }
}
</style>
